// frontend/src/components/chat/MaterialAllocationPanel.jsx

import React, { useState, useEffect } from 'react';

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildInitialAllocations(editedMaterials, matchedJobs) {
  const alloc = {};
  for (const mat of editedMaterials) {
    const totalOrdered = matchedJobs.reduce((sum, job) => {
      const m = job.materials?.find(m => m.type === mat.type);
      return sum + (m?.orderedQty ?? 0);
    }, 0);

    alloc[mat.type] = {};
    let remaining = mat.receivedQty;

    matchedJobs.forEach((job, idx) => {
      const jobOrdered = job.materials?.find(m => m.type === mat.type)?.orderedQty ?? 0;
      const isLast = idx === matchedJobs.length - 1;
      const qty = isLast
        ? remaining
        : totalOrdered > 0
          ? Math.round(mat.receivedQty * jobOrdered / totalOrdered)
          : Math.round(mat.receivedQty / matchedJobs.length);
      alloc[mat.type][job.jobId] = qty;
      remaining -= qty;
    });
  }
  return alloc;
}

function rowRemaining(mat, allocations, matchedJobs) {
  const allocated = matchedJobs.reduce((s, j) => s + (Number(allocations[mat.type]?.[j.jobId]) || 0), 0);
  return mat.receivedQty - allocated;
}

function jobColumnTotal(job, allocations, editedMaterials) {
  return editedMaterials.reduce((s, mat) => s + (Number(allocations[mat.type]?.[job.jobId]) || 0), 0);
}

function jobOrdered(job, editedMaterials) {
  return editedMaterials.reduce((s, mat) => {
    const m = job.materials?.find(m => m.type === mat.type);
    return s + (m?.orderedQty ?? 0);
  }, 0);
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusDot({ remaining }) {
  if (remaining === 0) return (
    <svg className="text-emerald-500 flex-shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
  if (remaining > 0) return <span className="text-xs font-semibold text-amber-500">+{remaining}</span>;
  return <span className="text-xs font-semibold text-red-500">{remaining}</span>;
}

function JobDeltaBadge({ delta }) {
  if (delta === 0) return (
    <svg className="text-emerald-500 mx-auto" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
  const sign = delta > 0 ? '+' : '';
  const cls = delta > 0 ? 'text-red-500' : 'text-amber-500';
  return <span className={`text-xs font-semibold ${cls}`}>{sign}{delta}</span>;
}

// ── Single-job layout ──────────────────────────────────────────────────────────

function SingleJobView({ job, editedMaterials }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-6 h-6 rounded-md bg-tint flex items-center justify-center flex-shrink-0">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
        </div>
        <div>
          <div className="text-xs font-semibold text-ink">Job #{job.jobId}</div>
          <div className="text-xs text-muted">{job.issuerName}</div>
        </div>
        <div className="ml-auto text-xs text-muted">All quantities → this job</div>
      </div>

      <div className="rounded-lg border border-trim overflow-hidden">
        <div className="grid bg-wash border-b border-trim px-3 py-1.5" style={{ gridTemplateColumns: '1fr 80px 80px 24px' }}>
          <span className="text-xs font-semibold text-copy uppercase tracking-wide">Material</span>
          <span className="text-xs font-semibold text-copy uppercase tracking-wide text-right">Received</span>
          <span className="text-xs font-semibold text-copy uppercase tracking-wide text-right">Ordered</span>
          <span />
        </div>
        {editedMaterials.map((mat, i) => {
          const ordered = job.materials?.find(m => m.type === mat.type)?.orderedQty ?? 0;
          const delta = mat.receivedQty - ordered;
          return (
            <div
              key={mat.type}
              className={`grid px-3 py-2 items-center ${i < editedMaterials.length - 1 ? 'border-b border-trim' : ''}`}
              style={{ gridTemplateColumns: '1fr 80px 80px 24px' }}
            >
              <span className="text-xs text-ink truncate pr-2">{mat.type}</span>
              <span className="text-sm font-semibold text-ink text-right">{mat.receivedQty.toLocaleString()}</span>
              <span className="text-xs text-copy text-right">{ordered.toLocaleString()}</span>
              <div className="flex items-center justify-end">
                <JobDeltaBadge delta={delta} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Multi-job layout ───────────────────────────────────────────────────────────

function MultiJobView({ matchedJobs, editedMaterials, allocations, onChange }) {
  const jobCount = matchedJobs.length;
  // Grid: material label | total | [job cols] | remaining
  const gridCols = `1fr 60px ${matchedJobs.map(() => '72px').join(' ')} 36px`;

  return (
    <div>
      <div className="rounded-lg border border-trim overflow-visible">
        {/* Header */}
        <div
          className="grid bg-wash border-b border-trim px-3 py-1.5 gap-1.5 items-center"
          style={{ gridTemplateColumns: gridCols }}
        >
          <span className="text-xs font-semibold text-copy uppercase tracking-wide">Material</span>
          <span className="text-xs font-semibold text-copy uppercase tracking-wide text-center">Total</span>
          {matchedJobs.map(job => (
            <span key={job.jobId} className="text-xs font-semibold text-ink text-center truncate" title={`Job #${job.jobId}`}>
              #{job.jobId}
            </span>
          ))}
          <span className="text-xs font-semibold text-copy uppercase tracking-wide text-center">Left</span>
        </div>

        {/* Material rows */}
        {editedMaterials.map((mat, i) => {
          const rem = rowRemaining(mat, allocations, matchedJobs);
          const isLast = i === editedMaterials.length - 1;
          return (
            <div
              key={mat.type}
              className={`grid px-3 py-2 gap-1.5 items-center ${!isLast ? 'border-b border-trim' : ''}`}
              style={{ gridTemplateColumns: gridCols }}
            >
              <span className="text-xs text-ink truncate pr-1" title={mat.type}>{mat.type}</span>
              <span className="text-sm font-semibold text-ink text-center">{mat.receivedQty.toLocaleString()}</span>
              {matchedJobs.map(job => (
                <input
                  key={job.jobId}
                  type="number"
                  min="0"
                  value={allocations[mat.type]?.[job.jobId] ?? ''}
                  onChange={e => onChange(mat.type, job.jobId, e.target.value)}
                  className={`w-full px-1.5 py-1 rounded-md border text-sm text-center font-semibold outline-none transition-colors ${
                    rem === 0
                      ? 'border-emerald-300 text-emerald-700 bg-emerald-50 focus:border-emerald-400'
                      : rem > 0
                        ? 'border-amber-300 text-amber-700 bg-amber-50 focus:border-amber-400'
                        : 'border-red-300 text-red-700 bg-red-50 focus:border-red-400'
                  }`}
                />
              ))}
              <div className="flex items-center justify-center">
                <StatusDot remaining={rem} />
              </div>
            </div>
          );
        })}

        {/* Job summary footer */}
        <div className="border-t-2 border-trim bg-wash">
          {/* Allocated totals */}
          <div
            className="grid px-3 py-1.5 gap-1.5 items-center border-b border-trim"
            style={{ gridTemplateColumns: gridCols }}
          >
            <span className="text-xs font-semibold text-copy uppercase tracking-wide">Allocated</span>
            <span />
            {matchedJobs.map(job => {
              const total = jobColumnTotal(job, allocations, editedMaterials);
              return (
                <span key={job.jobId} className="text-xs font-semibold text-ink text-center">
                  {total.toLocaleString()}
                </span>
              );
            })}
            <span />
          </div>
          {/* Ordered totals */}
          <div
            className="grid px-3 py-1.5 gap-1.5 items-center border-b border-trim"
            style={{ gridTemplateColumns: gridCols }}
          >
            <span className="text-xs font-semibold text-copy uppercase tracking-wide">Ordered</span>
            <span />
            {matchedJobs.map(job => {
              const ord = jobOrdered(job, editedMaterials);
              return (
                <span key={job.jobId} className="text-xs text-copy text-center">
                  {ord.toLocaleString()}
                </span>
              );
            })}
            <span />
          </div>
          {/* Delta row */}
          <div
            className="grid px-3 py-1.5 gap-1.5 items-center"
            style={{ gridTemplateColumns: gridCols }}
          >
            <span className="text-xs font-semibold text-copy uppercase tracking-wide">Δ</span>
            <span />
            {matchedJobs.map(job => {
              const allocated = jobColumnTotal(job, allocations, editedMaterials);
              const ordered = jobOrdered(job, editedMaterials);
              return (
                <div key={job.jobId} className="flex items-center justify-center">
                  <JobDeltaBadge delta={allocated - ordered} />
                </div>
              );
            })}
            <span />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

export function MaterialAllocationPanel({ open, onClose, onSave, onReviewed, result, editedMaterials }) {
  const matchedJobs = result?.matchedJobs ?? [];
  const isMultiJob = matchedJobs.length > 1;

  const [allocations, setAllocations] = useState({});

  useEffect(() => {
    if (result && editedMaterials?.length) {
      setAllocations(buildInitialAllocations(editedMaterials, matchedJobs));
    }
  }, [result, editedMaterials]);

  function handleChange(materialType, jobId, rawVal) {
    const val = rawVal === '' ? '' : Math.max(0, parseInt(rawVal) || 0);
    setAllocations(prev => ({
      ...prev,
      [materialType]: { ...prev[materialType], [jobId]: val },
    }));
  }

  const allRowsBalanced = editedMaterials?.every(mat => rowRemaining(mat, allocations, matchedJobs) === 0) ?? false;

  function handleDone() {
    const perJobAllocations = matchedJobs.map(job => ({
      jobId: job.jobId,
      issuerName: job.issuerName,
      eventDate: job.eventDate,
      materials: (editedMaterials ?? []).map(mat => ({
        type: mat.type,
        allocatedQty: Number(allocations[mat.type]?.[job.jobId]) || 0,
        orderedQty: job.materials?.find(m => m.type === mat.type)?.orderedQty ?? 0,
      })),
    }));
    onSave?.(perJobAllocations);
    onReviewed?.();
    onClose();
  }

  const totalReceived = (editedMaterials ?? []).reduce((s, m) => s + m.receivedQty, 0);

  const subTitle = isMultiJob
    ? `${totalReceived.toLocaleString()} received · ${matchedJobs.length} jobs`
    : matchedJobs[0] ? `Job #${matchedJobs[0].jobId} · ${matchedJobs[0].issuerName}` : '';

  return (
    <>
      {open && <div className="fixed inset-0 z-[105]" onClick={onClose} />}

      <div
        className="fixed top-0 right-0 h-full z-[110] bg-white border-l border-trim flex flex-col transition-transform duration-300"
        style={{
          width: isMultiJob ? Math.min(440 + matchedJobs.length * 72, 640) : 420,
          transform: open ? 'translateX(0)' : 'translateX(calc(100% + 2px))',
          boxShadow: open ? '-4px 0 24px rgba(4,50,67,.12)' : 'none',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-trim flex-shrink-0">
          <div className="flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
            <span className="text-sm font-semibold text-ink">Material Allocation</span>
            <span className="text-xs text-muted">·</span>
            <span className="text-xs text-muted">{subTitle}</span>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-md text-muted hover:bg-wash hover:text-ink transition-colors"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isMultiJob ? (
            <>
              {/* Instruction banner */}
              <div className="flex items-start gap-2 mb-4 px-3 py-2 bg-tint rounded-lg border border-teal-200">
                <svg className="flex-shrink-0 mt-0.5 text-teal-600" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p className="text-xs text-teal-700 leading-relaxed">
                  Enter how many of each material go to each job. Each row must sum to the total received. Pre-filled proportionally to ordered quantities.
                </p>
              </div>
              <MultiJobView
                matchedJobs={matchedJobs}
                editedMaterials={editedMaterials ?? []}
                allocations={allocations}
                onChange={handleChange}
              />
            </>
          ) : (
            <SingleJobView
              job={matchedJobs[0]}
              editedMaterials={editedMaterials ?? []}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 pt-2.5 border-t border-trim flex-shrink-0">
          {isMultiJob && !allRowsBalanced && (
            <div className="flex items-center gap-1.5 mb-2 text-xs text-amber-600">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Some rows are not fully allocated.
            </div>
          )}
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-1.5 rounded-lg border border-trim text-xs font-medium text-copy hover:bg-wash transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDone}
              className="flex-1 py-1.5 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-md hover:shadow-teal-500/25 transition-all"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
